import atlas, {
  AuthenticationType,
  type MapMouseEvent,
  type MapMouseWheelEvent,
  type MapTouchEvent
} from 'azure-maps-control'
import { watch } from 'vue'
import { useMapCoordinateSelectionStore } from '@/stores/mapCoordinateSelection'
import { useNetworkStore } from '@/stores/network'
import type { POI } from '@/contracts/POI'
import * as cfg from 'cfg'
import { usePOIStore } from '@/stores/POI'
import type { Network } from '@/contracts/network'
import type { GeoJsonLineFeature, GeoJsonPointFeature, Route } from '@/contracts/route'
import {
  buildAndAddGenericMapDataSources,
  buildAndAddNetworkDataSources,
  debugRouteOuterPointsDataSource,
  debugRoutePointsDataSource,
  endPointDataSource,
  entryExitRouteDataSOurce,
  locationsDataSource,
  type NetworkDataSources,
  removeNetworkDataSources,
  roadWorksDataSource,
  routeDataSource,
  routeLocationsDataSource,
  routeRoadWorksDataSource,
  routeStructuresDataSource,
  startPointDataSource,
  structuresDataSource
} from '@/utils/map/trex-map-datasource-builder'
import { buildAndAddCustomMapIcons } from '@/utils/map/map-icon-builder'
import {
  buildAndAddGenericLayers,
  buildAndAddNetworkLayers,
  locationsLayerId,
  removeNetworkLayers,
  roadWorksLayerId,
  routeLayerId,
  segmentsLayerId,
  structuresLayerId
} from '@/utils/map/trex-map-layer-builder'
import { popup } from '@/utils/map/map-layer-builder'
import { filterLocations, filterStructures } from '@/utils/POI-utils'
import { useRoadWorksStore } from '@/stores/roadWorks'
import type { RoadWorks } from '@/contracts/road-works'
import { formatDate } from '@/utils/date-utils'
import { getIndexOfLineStringIntersectingWithPoint } from '@/utils/map/point-calculation-utils'
import { POIType } from '@/enums/POI-type'
import Konami from 'konami'
import { useDebugStore } from '@/stores/debug'
import type { OsmWayFeature } from '@/contracts/osm'
import { buildAndAddDataSource } from '@/utils/map/map-datasource-builder'
import { createLineLayer } from '@/utils/map/map-layer-factory'
import { removeLayer } from '@/utils/map/map-layer-builder'
import { prepareNetworkFeatures } from '@/utils/map/geometry-optimization-utils'
import Feature = atlas.data.Feature

export const defaultZoom: number = 7.5
export const defaultCenterLongitude: number = 4.469936
export const defaultCenterLatitude: number = 50.503887

export let map: atlas.Map | undefined

type PointType = 'entry' | 'exit'

// Networks
const networkDataSources: { [networkId: string]: NetworkDataSources } = {}

// OSM segment selection overlay
const osmOverlayLayerId = 'osmSegmentsOverlayLayer'
const osmSelectedLayerId = 'osmSegmentsSelectedLayer'
const osmHoverLayerId = 'osmSegmentsHoverLayer'
const osmRemoveLayerId = 'osmSegmentsRemoveLayer'
const osmLeafLayerId = 'osmSegmentsLeafLayer'
let osmOverlayDataSource: atlas.source.DataSource | null = null
let osmSelectedDataSource: atlas.source.DataSource | null = null
let osmHoverDataSource: atlas.source.DataSource | null = null
let osmRemoveDataSource: atlas.source.DataSource | null = null
let osmLeafDataSource: atlas.source.DataSource | null = null
let osmRemoveClickCallback: ((gid: number) => void) | null = null
let osmRemoveLayer: atlas.layer.Layer | null = null
let osmRemoveClickHandler: LayerEventCallback | null = null
let osmRemoveMouseOverHandler: LayerEventCallback | null = null
let osmRemoveMouseOutHandler: LayerEventCallback | null = null
let osmSegmentClickCallback: ((wayId: number) => void) | null = null
let osmOverlayClickHandler: LayerEventCallback | null = null
let osmOverlayMouseOverHandler: LayerEventCallback | null = null
let osmOverlayMouseOutHandler: LayerEventCallback | null = null
let osmViewportChangeCallback: (() => void) | null = null
let osmMoveEndHandler: (() => void) | null = null
let osmFeaturesCache: OsmWayFeature[] = []

// OSM edit mode state
let isOsmEditModeActive = false // Track if OSM edit mode is active

let networkSegmentClickNetworkId: number | null = null
let networkSegmentClickLayer: atlas.layer.Layer | null = null
let networkSegmentClickHandler: LayerEventCallback | null = null
let networkSegmentMouseOverHandler: LayerEventCallback | null = null
let networkSegmentMouseOutHandler: LayerEventCallback | null = null

const delay = async (ms = 1000) => new Promise((resolve) => setTimeout(resolve, ms))

export async function initMap(mapDiv: HTMLDivElement) {
  const mapCoordinateSelectionStore = useMapCoordinateSelectionStore()
  const debugStore = useDebugStore()

  map = new atlas.Map(mapDiv, {
    center: [defaultCenterLongitude, defaultCenterLatitude],
    zoom: defaultZoom,
    authOptions: {
      authType: AuthenticationType.subscriptionKey,
      subscriptionKey: cfg.config.azureMapsSubscriptionKey
    },
    pixelRatio: window.devicePixelRatio || 1,
    enableAccessibility: false,
    style: 'road'
  })

  watch(
    () => mapCoordinateSelectionStore.isAllowedToSetCoordinates,
    () => {
      updateMapCursor()
    }
  )

  map.events.add('ready', async () => {
    const POIStore = usePOIStore()
    const networkStore = useNetworkStore()

    await buildAndAddCustomMapIcons(map!)

    buildAndAddGenericMapDataSources(map!)
    buildAndAddGenericLayers(
      map!,
      structuresDataSource,
      locationsDataSource,
      routeDataSource,
      entryExitRouteDataSOurce,
      startPointDataSource,
      endPointDataSource,
      routeStructuresDataSource,
      routeLocationsDataSource,
      routeRoadWorksDataSource,
      debugRoutePointsDataSource
    )

    await networkStore.fetchNetworks()

    networkStore.networks.forEach((network: Network) => {
      addNetworkLayersAndDataSources(network.id)
    })

    if (POIStore.POIsVisible) {
      await getPOIsAndAddToMap() // add global POI's
    }

    map!.events.add('click', (event: MapMouseEvent) => {
      mapCoordinateSelectionStore.setSelectedCoordinates(event.position![0], event.position![1])
    })

    if (debugStore.isDebuggingEnabled && debugStore.trackCoordinates) {
      map!.events.add('mousemove', (event: MapMouseEvent) => {
        debugStore.latitude = event.position![1].toFixed(6)
        debugStore.longitude = event.position![0].toFixed(6)
      })
    }

    // Setup zoom/pan event listener for automatic network re-rendering
    setupNetworkRerenderListener()
  })

  new Konami(() => toggleStyle())
}

/**
 * Note: Zoom-based re-rendering is no longer needed.
 * Network segments now use layer filter expressions that automatically
 * show/hide features based on zoom level, eliminating the need for
 * expensive clear/re-add operations on zoom changes.
 */
function setupNetworkRerenderListener() {
  // No-op: Layer expressions now handle zoom-based filtering
  // This function is kept for backwards compatibility but does nothing
}

export function resetMap() {
  map!.setCamera({
    center: [defaultCenterLongitude, defaultCenterLatitude],
    zoom: defaultZoom
  })
}

export function updateMapCursor() {
  const canvas = map?.getCanvas()
  if (!canvas) return

  const mapCoordinateSelectionStore = useMapCoordinateSelectionStore()
  canvas.style.cursor = mapCoordinateSelectionStore.isAllowedToSetCoordinates ? 'crosshair' : 'grab'
}

export async function addNetworkToMap(networkId: number) {
  const networkStore = useNetworkStore()
  const POIStore = usePOIStore()

  const network = networkStore.networks.find((n) => n.id === networkId)

  const dataSources = networkDataSources[networkId]

  if (dataSources && network) {
    if (network.geoData && network.geoData.features && network.geoData.features.length > 0) {
      await renderNetworkSegments(networkId, network, dataSources)
    }

    if (POIStore.POIsVisible) {
      renderNetworkPOIs(networkId)
    }
  }
}

export async function refreshNetworkSegments(networkId: number): Promise<void> {
  const networkStore = useNetworkStore()
  const network = networkStore.networks.find((n) => n.id === networkId)
  const dataSources = networkDataSources[networkId]
  if (!dataSources || !network?.geoData?.features?.length) return
  dataSources.networkDataSource.clear()
  await renderNetworkSegments(networkId, network, dataSources)
}

export function addRouteToMap(route: Route) {
  if (route && route.features && route.features.length > 0) {
    const debugStore = useDebugStore()

    addRouteFeaturesToMap(route)

    if (route.startPointFeature) {
      if (debugStore.isDebuggingEnabled) {
        route.startPointFeature.properties = {
          ...route.startPointFeature.properties,
          desc: `${route.startPointFeature.properties.desc}:
          ${route.startPointFeature.geometry.coordinates[1]} - ${route.startPointFeature.geometry.coordinates[0]}`
        }
      }
      startPointDataSource.add(route.startPointFeature)
    }

    if (route.endPointFeature) {
      if (debugStore.isDebuggingEnabled) {
        route.endPointFeature.properties = {
          ...route.endPointFeature.properties,
          desc: `${route.endPointFeature.properties.desc}:
          ${route.endPointFeature.geometry.coordinates[1]} - ${route.endPointFeature.geometry.coordinates[0]}`
        }
      }
      endPointDataSource.add(route.endPointFeature)
    }

    if (route.entryLineFeature && route.exitLineFeature) {
      entryExitRouteDataSOurce.add([route.entryLineFeature, route.exitLineFeature])
    }

    if (route.pois && route.pois.length > 0) {
      const structures = filterStructures(route.pois)
      const locations = filterLocations(route.pois)

      addPOIsToMap(structures, locations, undefined, true)
    }

    if (route.roadWorks && route.roadWorks.length > 0) {
      addRoadWorksToMap(route.roadWorks, true)
    }
  }
}

function addRouteFeaturesToMap(route: Route) {
  fixRouteSegment(route.startPointFeature, route.entryLineFeature, route.features, 'entry')
  fixRouteSegment(route.endPointFeature, route.exitLineFeature, route.features, 'exit')

  const debugStore = useDebugStore()

  if (debugStore.isDebuggingEnabled && debugStore.showRouteSegments) {
    const points: any[] = []
    const outerPoints: any[] = []

    route.features.forEach((f) => {
      f.geometry.coordinates.forEach((coordinate, index) => {
        const point = new atlas.data.Point([coordinate[0], coordinate[1]])
        const feature = new atlas.data.Feature(point, {
          desc: `<br/>${coordinate[1]} - ${coordinate[0]}`
        })

        if (index === 0 || index === f.geometry.coordinates.length - 1) {
          outerPoints.push(feature)
        } else {
          points.push(feature)
        }
      })
    })

    debugRoutePointsDataSource.add(points)
    debugRouteOuterPointsDataSource.add(outerPoints)
  }

  routeDataSource.add(route.features)
}

function fixRouteSegment(
  startOrEndPointFeature: GeoJsonPointFeature,
  entryOrExitLineFeature: GeoJsonLineFeature,
  routeFeatures: GeoJsonLineFeature[],
  pointType: PointType
) {
  let featureIndex = -1
  let lineStringIndex = -1

  // Get coordinates of point where entry or exit line intersect with route
  const routeIntersectionPoint = getIntersectionPoint(
    startOrEndPointFeature,
    entryOrExitLineFeature
  )

  if (routeIntersectionPoint) {
    routeFeatures.forEach((routeFeature, index) => {
      const indexOfLineStringContainingPoint = getIndexOfLineStringIntersectingWithPoint(
        routeIntersectionPoint!,
        routeFeature.geometry.coordinates
      )
      if (indexOfLineStringContainingPoint !== -1) {
        featureIndex = index
        lineStringIndex = indexOfLineStringContainingPoint
      }
    })

    if (featureIndex === -1) {
      addCustomSegments(pointType, routeFeatures, routeIntersectionPoint)
    } else {
      removeObsoleteSegments(
        pointType,
        routeFeatures[featureIndex],
        lineStringIndex,
        routeIntersectionPoint
      )
    }
  }
}

function getIntersectionPoint(pointFeature: GeoJsonPointFeature, lineFeature: GeoJsonLineFeature) {
  return lineFeature.geometry.coordinates.find(
    (coordinates: [number, number]) =>
      pointFeature.geometry.coordinates[0] !== coordinates[0] &&
      pointFeature.geometry.coordinates[1] !== coordinates[1]
  )
}

function removeObsoleteSegments(
  pointType: PointType,
  feature: GeoJsonLineFeature,
  indexOfLineContainingPoint: number,
  startOrEndPointCoordinates: [number, number]
) {
  switch (pointType) {
    case 'entry':
      // Remove the obsolete segments
      feature.geometry.coordinates = feature.geometry.coordinates.slice(indexOfLineContainingPoint)
      // Cut first segment that is left over
      feature.geometry.coordinates[0] = startOrEndPointCoordinates
      break
    case 'exit':
      feature.geometry.coordinates = feature.geometry.coordinates.slice(
        0,
        indexOfLineContainingPoint + 2
      )
      break
    default:
      break
  }
}

function addCustomSegments(
  pointType: PointType,
  features: GeoJsonLineFeature[],
  point: [number, number]
) {
  switch (pointType) {
    case 'entry':
      features.at(0)!.geometry.coordinates.unshift(point)
      break
    case 'exit':
      features.at(-1)!.geometry.coordinates.push(point)
      break
    default:
      break
  }
}

function addPOIsToMap(
  structures: POI[],
  locations: POI[],
  networkId?: number,
  routePOIs: boolean = false
) {
  const structureFeatures: Feature<
    atlas.data.Point,
    {
      id: number
      type: POIType
      text?: string
      title: string
      desc: string
      height?: string
    }
  >[] = []

  for (const structure of structures) {
    const point = new atlas.data.Point([
      parseFloat(structure.longitude),
      parseFloat(structure.latitude)
    ])
    structureFeatures.push(
      new atlas.data.Feature(point, {
        id: structure.id,
        type: POIType.Structure,
        text: structure.height,
        title: structure.name,
        desc: structure.description,
        height: structure.height
      })
    )
  }

  if (!networkId) {
    if (routePOIs) {
      routeStructuresDataSource.add(structureFeatures)
    } else {
      structuresDataSource.add(structureFeatures)
    }
  } else {
    const dataSources = networkDataSources[networkId]
    dataSources.structuresDataSource.add(structureFeatures)
  }

  const locationFeatures: Feature<
    atlas.data.Point,
    {
      id: number
      type: POIType
      title: string
      desc: string
    }
  >[] = []

  for (const location of locations) {
    const point = new atlas.data.Point([
      parseFloat(location.longitude),
      parseFloat(location.latitude)
    ])
    locationFeatures.push(
      new atlas.data.Feature(point, {
        id: location.id,
        type: POIType.Location,
        title: location.name,
        desc: location.description
      })
    )
  }

  if (!networkId) {
    if (routePOIs) {
      routeLocationsDataSource.add(locationFeatures)
    } else {
      locationsDataSource.add(locationFeatures)
    }
  } else {
    const dataSources = networkDataSources[networkId]
    dataSources.locationsDataSource.add(locationFeatures)
  }
}

export async function getPOIsAndAddToMap(networkId?: number) {
  const POIStore = usePOIStore()
  const networkStore = useNetworkStore()

  if (!POIStore.POIs?.get(networkId)?.length) {
    await POIStore.fetchPOIs(networkId)
  }

  const structures = POIStore.getStructures(networkId)
  const locations = POIStore.getLocations(networkId)

  // Clear existing POIs from the map before adding new ones
  removePOIsFromMap(networkId)

  // Move layers so that layers are in the correct sequence
  networkStore.selectedNetworks.forEach((selectedNetworkId: number) => {
    map!.layers.move(`${structuresLayerId}-${selectedNetworkId}`, routeLayerId)
    map!.layers.move(`${locationsLayerId}-${selectedNetworkId}`, routeLayerId)
  })

  addPOIsToMap(structures, locations, networkId)
}

function addRoadWorksToMap(roadWorksList: RoadWorks[], routeRoadWorks: boolean = false) {
  const roadWorksFeatures: Feature<
    atlas.data.Point,
    {
      id: number
      type: POIType
      text?: string
      title: string
      desc: string
    }
  >[] = []

  for (const roadWorks of roadWorksList) {
    const point = new atlas.data.Point([
      parseFloat(roadWorks.longitude),
      parseFloat(roadWorks.latitude)
    ])

    let title = roadWorks.owner

    if (roadWorks.startDate || roadWorks.endDate) {
      title += ' ('
      title += roadWorks.startDate ? formatDate(roadWorks.startDate) : ''
      title += ' - '
      title += roadWorks.endDate ? formatDate(roadWorks.endDate) : ''
      title += ')'
    }

    let description = `${roadWorks.title}<br/><br/>${roadWorks.details || ''}`

    // Add height and width limitations if they exist
    if (roadWorks.heightLimitation) {
      description += `<br/><br/>Hoogte beperking: ${roadWorks.heightLimitation} cm`
    }

    if (roadWorks.widthLimitation) {
      description += `<br/>${roadWorks.heightLimitation ? '' : '<br/>'}Breedte beperking: ${roadWorks.widthLimitation} cm`
    }

    roadWorksFeatures.push(
      new atlas.data.Feature(point, {
        id: roadWorks.id,
        type: POIType.RoadWorks,
        title: title,
        desc: description
      })
    )
  }

  if (routeRoadWorks) {
    routeRoadWorksDataSource.add(roadWorksFeatures)
  } else {
    roadWorksDataSource.add(roadWorksFeatures)
  }
}

export async function getRoadWorksAndAddToMap() {
  const roadWorkStore = useRoadWorksStore()
  const networkStore = useNetworkStore()

  if (!roadWorkStore.roadWorks.length) {
    await roadWorkStore.fetchRoadworks()
  }

  //move layers so that layers are in correct sequence
  networkStore.selectedNetworks.forEach(() => {
    map!.layers.move(roadWorksLayerId, routeLayerId)
    map!.layers.move(`${roadWorksLayerId}-clustered`, roadWorksLayerId)
  })

  removeRoadWorksFromMap()
  addRoadWorksToMap(roadWorkStore.roadWorks)
}

const NETWORK_SEGMENTS_CHUNK_SIZE = 50000

function convertGeoJsonFeatureToAtlasFeature(
  f: GeoJsonLineFeature
): atlas.data.Feature<atlas.data.LineString, Record<string, unknown>> {
  const coords = f.geometry.coordinates as [number, number][]
  const lineString = new atlas.data.LineString(coords)
  const props: Record<string, unknown> = {}
  if (f.properties) {
    Object.assign(props, f.properties)
  }
  return new atlas.data.Feature(lineString, props)
}

async function renderNetworkSegments(
  networkId: number,
  network: Network,
  dataSources: NetworkDataSources
) {
  const networkStore = useNetworkStore()
  const debugStore = useDebugStore()

  networkStore.selectedNetworks.forEach((selectedNetworkId: number) => {
    map!.layers.move(`${segmentsLayerId}-${selectedNetworkId}`, `${segmentsLayerId}-${networkId}`)
    map!.layers.move(roadWorksLayerId, `${segmentsLayerId}-${selectedNetworkId}`)
    map!.layers.move(`${roadWorksLayerId}-clustered`, roadWorksLayerId)
  })

  // Prepare features with sample groups for zoom-based filtering via layer expressions
  // In OSM edit mode, we still need all segments but with sample groups for consistency
  const features = prepareNetworkFeatures(network.geoData!.features)

  // Render optimized features in chunks
  for (let i = 0; i < features.length; i += NETWORK_SEGMENTS_CHUNK_SIZE) {
    const chunk = features.slice(i, i + NETWORK_SEGMENTS_CHUNK_SIZE)
    const atlasFeatures = chunk.map(convertGeoJsonFeatureToAtlasFeature)
    dataSources.networkDataSource.add(atlasFeatures)

    // Only add delay for very large chunks to keep UI responsive
    if (features.length > NETWORK_SEGMENTS_CHUNK_SIZE) {
      await delay(0) // Yield to browser for painting
    }
  }

  if (debugStore.isDebuggingEnabled && debugStore.showNetworkFileMetadata) {
    const outerPoints: any[] = []

    // Use original features for debug points (not optimized)
    network.geoData!.features.forEach((f) => {
      f.geometry.coordinates.forEach((coordinate, index) => {
        const point = new atlas.data.Point([coordinate[0], coordinate[1]])
        const feature = new atlas.data.Feature(point, {
          title: f.metadata.name,
          desc: `
                    Lengte: ${f.metadata.length}
                    Breedte: ${f.metadata.width}
                    Hoogte: ${f.metadata.height}
                    Gewicht: ${f.metadata.weight}
                    `
        })

        if (index === 0 || index === f.geometry.coordinates.length - 1) {
          outerPoints.push(feature)
        }
      })
    })

    dataSources.debugNetworkPointsDataSource.add(outerPoints)
  }
}

function renderNetworkPOIs(networkId: number) {
  const POIStore = usePOIStore()
  const networkStore = useNetworkStore()

  // Add this line to clear existing POIs for this network
  removePOIsFromMap(networkId)

  networkStore.selectedNetworks.forEach((selectedNetworkId: number) => {
    map!.layers.move(
      `${structuresLayerId}-${selectedNetworkId}`,
      `${structuresLayerId}-${networkId}`
    )
    map!.layers.move(`${locationsLayerId}-${selectedNetworkId}`, `${locationsLayerId}-${networkId}`)
  })

  const POIs = POIStore.POIs.get(networkId)

  if (POIs) {
    const structures = filterStructures(POIs)
    const locations = filterLocations(POIs)
    addPOIsToMap(structures, locations, networkId)
  }
}

export function removeNetworkFromMap(networkId: number) {
  const dataSources = networkDataSources[networkId]

  if (dataSources) {
    dataSources.networkDataSource.clear()
    dataSources.structuresDataSource.clear()
    dataSources.locationsDataSource.clear()
    dataSources.debugNetworkPointsDataSource.clear()
  }
}

export function removeRouteFromMap() {
  routeDataSource.clear()
  startPointDataSource.clear()
  endPointDataSource.clear()
  entryExitRouteDataSOurce.clear()
  routeStructuresDataSource.clear()
  routeLocationsDataSource.clear()
  routeRoadWorksDataSource.clear()
  debugRouteOuterPointsDataSource.clear()
  debugRoutePointsDataSource.clear()

  popup.close()
}

export function removePOIsFromMap(networkId?: number) {
  if (!networkId) {
    structuresDataSource.clear()
    locationsDataSource.clear()
  } else {
    const dataSources = networkDataSources[networkId]

    if (dataSources) {
      dataSources.structuresDataSource.clear()
      dataSources.locationsDataSource.clear()
    }
  }

  popup.close()
}

export function removeRoadWorksFromMap() {
  roadWorksDataSource.clear()
  popup.close()
}

export function addNetworkLayersAndDataSources(networkId: number) {
  if (map) {
    const networkStore = useNetworkStore()
    const network = networkStore.networks.find((n) => n.id === networkId)

    if (network) {
      const dataSources: NetworkDataSources = buildAndAddNetworkDataSources(map!)
      networkDataSources[networkId] = dataSources

      buildAndAddNetworkLayers(
        map!,
        networkId,
        network.color,
        dataSources.networkDataSource,
        dataSources.structuresDataSource,
        dataSources.locationsDataSource,
        dataSources.debugNetworkPointsDataSource
      )
    }
  }
}

export function removeNetworkLayersAndDataSources(networkId: number) {
  removeNetworkLayers(map!, networkId)

  removeNetworkDataSources(map!, networkDataSources[networkId])
  delete networkDataSources[networkId]
}

export function zoom(multiplier: number) {
  const currentZoom = map!.getCamera().zoom!
  const newZoom = currentZoom * multiplier
  map!.setCamera({
    zoom: newZoom,
    type: 'ease',
    duration: 200
  })
}

export function zoomAndCenterOnLocation(longitude: number, latitude: number, zoom: number) {
  map!.setCamera({
    center: [longitude, latitude],
    zoom: zoom
  })
  map!.triggerRepaint()
}

function toggleStyle() {
  map?.setStyle({
    style: map?.getStyle().style === 'road' ? 'night' : 'road'
  })
}

export function getMapBounds(): {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
} | null {
  if (!map) return null
  const canvas = map.getCanvas()
  if (!canvas) return null
  const topLeft = new atlas.Pixel(0, 0)
  const bottomRight = new atlas.Pixel(canvas.width, canvas.height)
  const positions = map.pixelsToPositions([topLeft, bottomRight])
  if (!positions || positions.length < 2) return null
  const lons = [positions[0][0], positions[1][0]]
  const lats = [positions[0][1], positions[1][1]]
  return {
    minLon: Math.min(...lons),
    minLat: Math.min(...lats),
    maxLon: Math.max(...lons),
    maxLat: Math.max(...lats)
  }
}

export function getMapZoom(): number | undefined {
  return map?.getCamera().zoom
}

function parseCoordinates(coordinates: unknown): [number, number][] {
  if (!Array.isArray(coordinates)) return []
  return coordinates.map((c) => {
    if (Array.isArray(c) && c.length >= 2) {
      return [Number(c[0]), Number(c[1])] as [number, number]
    }
    return [0, 0] as [number, number]
  })
}

type LayerEventCallback = (
  e: void | MapMouseEvent | MapTouchEvent | MapMouseWheelEvent | atlas.layer.Layer
) => void

function createLineFeature(
  coordinates: unknown,
  properties: Record<string, unknown>
): atlas.data.Feature<atlas.data.LineString, Record<string, unknown>> {
  const lineString = new atlas.data.LineString(parseCoordinates(coordinates))
  return new atlas.data.Feature(lineString, properties)
}

function createHoverFeature(
  wayId: number
): atlas.data.Feature<atlas.data.LineString, Record<string, unknown>> | null {
  const feature = osmFeaturesCache.find((c) => c.properties.id === wayId)
  if (!feature) return null

  return createLineFeature(feature.geometry.coordinates, { id: wayId })
}

function setupLayerEventHandlers(
  layer: atlas.layer.Layer,
  onHover: LayerEventCallback,
  onOut: LayerEventCallback,
  onClick?: LayerEventCallback,
  canvas?: HTMLCanvasElement
): void {
  if (!map || !canvas) return

  map.events.add('mouseover', layer, onHover)
  map.events.add('mouseout', layer, onOut)
  if (onClick) {
    map.events.add('click', layer, onClick)
  }
}

function removeLayerEventHandlers(
  layer: atlas.layer.Layer | null,
  handlers: {
    click?: LayerEventCallback
    mouseover?: LayerEventCallback
    mouseout?: LayerEventCallback
  }
): void {
  if (!map || !layer) return

  if (handlers.click) {
    map.events.remove('click', layer, handlers.click)
  }
  if (handlers.mouseover) {
    map.events.remove('mouseover', layer, handlers.mouseover)
  }
  if (handlers.mouseout) {
    map.events.remove('mouseout', layer, handlers.mouseout)
  }
}

export async function ensureOsmOverlayLayer(
  onSegmentClick: (wayId: number) => void,
  onRemoveSegmentClick?: (gid: number) => void
): Promise<void> {
  if (!map) return

  // Enable OSM edit mode - this disables optimizations for network rendering
  const wasEditMode = isOsmEditModeActive
  isOsmEditModeActive = true

  osmSegmentClickCallback = onSegmentClick
  osmRemoveClickCallback = onRemoveSegmentClick ?? null

  const canvas = map.getCanvas()

  function handleOsmClick(event: any) {
    const e = event as MapMouseEvent
    if (e?.shapes?.length) {
      const wayId = getWayIdFromShape(e.shapes[0])
      if (typeof wayId === 'number') osmSegmentClickCallback?.(wayId)
    }
  }

  function handleOsmHover(event: any) {
    const e = event as MapMouseEvent
    if (canvas) canvas.style.cursor = 'pointer'
    if (e?.shapes?.length) {
      const wayId = getWayIdFromShape(e.shapes[0])
      if (wayId != null) {
        const feature = createHoverFeature(wayId)
        if (feature && osmHoverDataSource) {
          osmHoverDataSource.clear()
          osmHoverDataSource.add(feature)
          map?.triggerRepaint()
        }
      }
    }
  }

  function handleOsmOut() {
    updateMapCursor()
    osmHoverDataSource?.clear()
    map?.triggerRepaint()
  }

  function handleRemoveClick(event: any) {
    const e = event as MapMouseEvent
    if (e?.shapes?.length) {
      const gid = getGidFromShape(e.shapes[0])
      if (typeof gid === 'number') osmRemoveClickCallback?.(gid)
    }
  }

  if (osmOverlayDataSource) {
    const overlayLayer = map.layers.getLayerById(osmOverlayLayerId)
    const selectedLayer = map.layers.getLayerById(osmSelectedLayerId)
    const removeLayerObj = map.layers.getLayerById(osmRemoveLayerId)

    if (overlayLayer) {
      removeLayerEventHandlers(overlayLayer, {
        click: osmOverlayClickHandler || undefined,
        mouseover: osmOverlayMouseOverHandler || undefined,
        mouseout: osmOverlayMouseOutHandler || undefined
      })
      osmOverlayClickHandler = handleOsmClick
      osmOverlayMouseOverHandler = handleOsmHover
      osmOverlayMouseOutHandler = handleOsmOut
      setupLayerEventHandlers(
        overlayLayer,
        osmOverlayMouseOverHandler,
        osmOverlayMouseOutHandler,
        osmOverlayClickHandler,
        canvas
      )
    }

    if (selectedLayer) {
      // Reuse handlers from overlay layer for consistency
      setupLayerEventHandlers(
        selectedLayer,
        osmOverlayMouseOverHandler!,
        osmOverlayMouseOutHandler!,
        osmOverlayClickHandler!,
        canvas
      )
    }

    if (removeLayerObj) {
      removeLayerEventHandlers(osmRemoveLayer!, {
        click: osmRemoveClickHandler || undefined,
        mouseover: osmRemoveMouseOverHandler || undefined,
        mouseout: osmRemoveMouseOutHandler || undefined
      })

      osmRemoveLayer = removeLayerObj
      osmRemoveClickHandler = handleRemoveClick
      osmRemoveMouseOverHandler = () => {
        if (canvas) canvas.style.cursor = 'pointer'
      }
      osmRemoveMouseOutHandler = () => {
        updateMapCursor()
      }

      setupLayerEventHandlers(
        removeLayerObj,
        osmRemoveMouseOverHandler,
        osmRemoveMouseOutHandler,
        osmRemoveClickHandler,
        canvas
      )
    }
    return
  }

  osmLeafDataSource = buildAndAddDataSource(map, false)
  const leafLayer = createLineLayer(
    osmLeafLayerId,
    osmLeafDataSource,
    OSM_LAYER_COLORS.leaf,
    OSM_LAYER_STROKE_WIDTHS.leaf
  )
  map.layers.add(leafLayer, routeLayerId)

  osmHoverDataSource = buildAndAddDataSource(map, false)
  const hoverLayer = createLineLayer(
    osmHoverLayerId,
    osmHoverDataSource,
    OSM_LAYER_COLORS.hover,
    OSM_LAYER_STROKE_WIDTHS.hover
  )
  map.layers.add(hoverLayer, routeLayerId)

  osmOverlayDataSource = buildAndAddDataSource(map, false)
  const lineLayer = createLineLayer(
    osmOverlayLayerId,
    osmOverlayDataSource,
    OSM_LAYER_COLORS.overlay,
    OSM_LAYER_STROKE_WIDTHS.overlay
  )
  map.layers.add(lineLayer, routeLayerId)

  osmSelectedDataSource = buildAndAddDataSource(map, false)
  const selectedLayer = createLineLayer(
    osmSelectedLayerId,
    osmSelectedDataSource,
    OSM_LAYER_COLORS.selected,
    OSM_LAYER_STROKE_WIDTHS.selected
  )
  map.layers.add(selectedLayer, routeLayerId)

  osmRemoveDataSource = buildAndAddDataSource(map, false)
  const removeLayerObj = createLineLayer(
    osmRemoveLayerId,
    osmRemoveDataSource,
    OSM_LAYER_COLORS.remove,
    OSM_LAYER_STROKE_WIDTHS.remove
  )
  map.layers.add(removeLayerObj, routeLayerId)
  osmRemoveLayer = removeLayerObj

  osmOverlayClickHandler = handleOsmClick
  osmOverlayMouseOverHandler = handleOsmHover
  osmOverlayMouseOutHandler = handleOsmOut
  osmRemoveClickHandler = handleRemoveClick
  osmRemoveMouseOverHandler = () => {
    if (canvas) canvas.style.cursor = 'pointer'
  }
  osmRemoveMouseOutHandler = () => {
    updateMapCursor()
  }

  setupLayerEventHandlers(
    lineLayer,
    osmOverlayMouseOverHandler,
    osmOverlayMouseOutHandler,
    osmOverlayClickHandler,
    canvas
  )
  setupLayerEventHandlers(
    selectedLayer,
    osmOverlayMouseOverHandler,
    osmOverlayMouseOutHandler,
    osmOverlayClickHandler,
    canvas
  )
  setupLayerEventHandlers(
    removeLayerObj,
    osmRemoveMouseOverHandler,
    osmRemoveMouseOutHandler,
    osmRemoveClickHandler,
    canvas
  )

  // Note: No need to re-render segments when entering edit mode.
  // The zoom filter is disabled in setNetworkSegmentsOsmMode() which makes all segments visible.
}

export function setOsmViewportChangeListener(callback: (() => void) | null): void {
  if (!map) return
  if (osmMoveEndHandler) {
    map.events.remove('moveend', osmMoveEndHandler)
    osmMoveEndHandler = null
  }
  osmViewportChangeCallback = callback
  if (callback) {
    osmMoveEndHandler = () => osmViewportChangeCallback?.()
    map.events.add('moveend', osmMoveEndHandler)
  }
}

export async function removeOsmOverlayLayer(): Promise<void> {
  if (!map) return

  // Disable OSM edit mode - re-enable optimizations for network rendering
  const wasEditMode = isOsmEditModeActive
  isOsmEditModeActive = false

  setOsmViewportChangeListener(null)
  osmSegmentClickCallback = null
  osmRemoveClickCallback = null
  osmFeaturesCache = []

  if (osmHoverDataSource) {
    removeLayer(map, osmHoverLayerId)
    map.sources.remove(osmHoverDataSource)
    osmHoverDataSource = null
  }
  if (osmSelectedDataSource) {
    const layer = map.layers.getLayerById(osmSelectedLayerId)
    if (layer) {
      removeLayerEventHandlers(layer, {
        click: osmOverlayClickHandler || undefined,
        mouseover: osmOverlayMouseOverHandler || undefined,
        mouseout: osmOverlayMouseOutHandler || undefined
      })
    }
    removeLayer(map, osmSelectedLayerId)
    map.sources.remove(osmSelectedDataSource)
    osmSelectedDataSource = null
  }
  if (osmOverlayDataSource) {
    const layer = map.layers.getLayerById(osmOverlayLayerId)
    if (layer) {
      removeLayerEventHandlers(layer, {
        click: osmOverlayClickHandler || undefined,
        mouseover: osmOverlayMouseOverHandler || undefined,
        mouseout: osmOverlayMouseOutHandler || undefined
      })
    }
    osmOverlayClickHandler = null
    osmOverlayMouseOverHandler = null
    osmOverlayMouseOutHandler = null
    removeLayer(map, osmOverlayLayerId)
    map.sources.remove(osmOverlayDataSource)
    osmOverlayDataSource = null
  }
  if (osmLeafDataSource) {
    removeLayer(map, osmLeafLayerId)
    map.sources.remove(osmLeafDataSource)
    osmLeafDataSource = null
  }
  if (osmRemoveDataSource) {
    if (osmRemoveLayer) {
      removeLayerEventHandlers(osmRemoveLayer, {
        click: osmRemoveClickHandler || undefined,
        mouseover: osmRemoveMouseOverHandler || undefined,
        mouseout: osmRemoveMouseOutHandler || undefined
      })
      osmRemoveClickHandler = null
      osmRemoveMouseOverHandler = null
      osmRemoveMouseOutHandler = null
      osmRemoveLayer = null
    }
    removeLayer(map, osmRemoveLayerId)
    map.sources.remove(osmRemoveDataSource)
    osmRemoveDataSource = null
  }

  // Note: No need to re-render segments when exiting edit mode.
  // The zoom filter is restored in setNetworkSegmentsOsmMode() which handles visibility.
}

const OSM_MODE_NETWORK_STROKE_FALLBACK = '#374151'
const NETWORK_SEGMENT_STROKE_WIDTH_NORMAL = 2
const NETWORK_SEGMENT_STROKE_WIDTH_EDIT_MODE = 6

// Zoom-based filter expression for network segments (matches map-layer-factory.ts)
const NETWORK_ZOOM_FILTER: atlas.Expression = [
  'any',
  ['>=', ['zoom'], 12],
  ['all', ['>=', ['zoom'], 10], ['<', ['get', '_sampleGroup'], 9]],
  ['all', ['>=', ['zoom'], 8], ['<', ['get', '_sampleGroup'], 8]],
  ['<', ['get', '_sampleGroup'], 6]
]

export function setNetworkSegmentsOsmMode(
  networkId: number,
  osmMode: boolean,
  strokeColor?: string
): void {
  if (!map) return
  const layer = map.layers.getLayerById(`${segmentsLayerId}-${networkId}`) as
    | atlas.layer.LineLayer
    | undefined
  if (!layer || typeof layer.setOptions !== 'function') return

  const color = strokeColor ?? OSM_MODE_NETWORK_STROKE_FALLBACK
  const strokeWidth = osmMode
    ? NETWORK_SEGMENT_STROKE_WIDTH_EDIT_MODE
    : NETWORK_SEGMENT_STROKE_WIDTH_NORMAL

  // In edit mode, disable zoom filter to show all segments for clicking
  // In normal mode, re-enable zoom filter for performance
  const filter = osmMode ? undefined : NETWORK_ZOOM_FILTER

  layer.setOptions({
    strokeDashArray: [1, 0],
    strokeColor: color,
    strokeWidth,
    filter
  })
  map.triggerRepaint()
}

export function moveNetworkSegmentLayerForOsmMode(networkId: number, onTopOfOsm: boolean): void {
  if (!map) return
  const layerId = `${segmentsLayerId}-${networkId}`
  if (!map.layers.getLayerById(layerId)) return
  if (onTopOfOsm && map.layers.getLayerById(osmHoverLayerId)) {
    map.layers.move(layerId, osmHoverLayerId)
  } else {
    map.layers.move(layerId, locationsLayerId)
  }
  map.triggerRepaint()
}

const OSM_LAYER_COLORS = {
  hover: '#fbbf24',
  overlay: '#f97316',
  selected: '#22c55e',
  remove: '#dc2626',
  leaf: '#facc15'
} as const

const OSM_LAYER_STROKE_WIDTHS = {
  hover: 5,
  overlay: 3,
  selected: 5,
  remove: 5,
  leaf: 8
} as const

type ShapeWithProperties = {
  getProperties?: () => Record<string, unknown>
  properties?: Record<string, unknown>
}

function getPropertyFromShape<T>(shape: unknown, key: string): T | undefined {
  const s = shape as ShapeWithProperties
  const props = s?.getProperties?.() ?? s?.properties
  const value = props?.[key]
  return typeof value === 'number' ? (value as T) : undefined
}

function getGidFromShape(shape: unknown): number | undefined {
  return getPropertyFromShape<number>(shape, 'gid')
}

function getWayIdFromShape(shape: unknown): number | undefined {
  return getPropertyFromShape<number>(shape, 'id')
}

export function setNetworkSegmentClickHandler(
  networkId: number,
  onSegmentClick: (gid: number) => void
): void {
  if (!map) return
  clearNetworkSegmentClickHandler()
  const layerId = `${segmentsLayerId}-${networkId}`
  const layer = map.layers.getLayerById(layerId)
  if (!layer) return
  const canvas = map.getCanvas()
  networkSegmentClickHandler = (event) => {
    const e = event as MapMouseEvent
    try {
      if (!e?.shapes?.length) return

      const clickedShape = e.shapes[0]
      const gid = getGidFromShape(clickedShape)
      if (typeof gid !== 'number') return

      if (osmRemoveDataSource && osmRemoveLayer) {
        const removeFeatures = osmRemoveDataSource.getShapes()
        const isInRemoveLayer = removeFeatures.some((shape: unknown) => {
          const shapeGid = getGidFromShape(shape)
          return shapeGid === gid
        })

        if (isInRemoveLayer) {
          return
        }
      }

      onSegmentClick(gid)
    } catch (err) {
      console.error('Error in network segment click handler:', err)
    }
  }
  networkSegmentMouseOverHandler = () => {
    if (canvas) canvas.style.cursor = 'pointer'
  }
  networkSegmentMouseOutHandler = () => {
    updateMapCursor()
  }
  map.events.add('click', layer, networkSegmentClickHandler)
  map.events.add('mouseover', layer, networkSegmentMouseOverHandler)
  map.events.add('mouseout', layer, networkSegmentMouseOutHandler)
  networkSegmentClickLayer = layer
  networkSegmentClickNetworkId = networkId
}

export function clearNetworkSegmentClickHandler(): void {
  if (!map || !networkSegmentClickLayer) return
  if (networkSegmentClickHandler) {
    map.events.remove('click', networkSegmentClickLayer, networkSegmentClickHandler)
    networkSegmentClickHandler = null
  }
  if (networkSegmentMouseOverHandler) {
    map.events.remove('mouseover', networkSegmentClickLayer, networkSegmentMouseOverHandler)
    networkSegmentMouseOverHandler = null
  }
  if (networkSegmentMouseOutHandler) {
    map.events.remove('mouseout', networkSegmentClickLayer, networkSegmentMouseOutHandler)
    networkSegmentMouseOutHandler = null
  }
  networkSegmentClickLayer = null
  networkSegmentClickNetworkId = null
}

export function setOsmOverlayFeatures(
  features: OsmWayFeature[],
  selectedIds: Set<number> = new Set()
): void {
  if (!osmOverlayDataSource || !osmSelectedDataSource) return
  osmFeaturesCache = features
  osmOverlayDataSource.clear()
  osmSelectedDataSource.clear()
  osmHoverDataSource?.clear()

  for (const f of features) {
    const feature = createLineFeature(f.geometry.coordinates, { id: f.properties.id })

    if (selectedIds.has(f.properties.id)) {
      osmSelectedDataSource.add(feature)
    } else {
      osmOverlayDataSource.add(feature)
    }
  }
}

export function updateOsmSelectedFeatures(selectedIds: Set<number>): void {
  if (!osmOverlayDataSource || !osmSelectedDataSource) return
  setOsmOverlayFeatures(osmFeaturesCache, selectedIds)
}

export function setOsmRemoveOverlayFeatures(
  networkFeatures: GeoJsonLineFeature[],
  gidsToRemove: Set<number>
): void {
  if (!osmRemoveDataSource) return
  osmRemoveDataSource.clear()
  for (const f of networkFeatures) {
    const gid = f.properties?.gid
    if (gid == null || !gidsToRemove.has(gid)) continue
    const feature = createLineFeature(f.geometry.coordinates, { gid })
    osmRemoveDataSource.add(feature)
  }
  map?.triggerRepaint()
}

export function setOsmLeafOverlayFeatures(
  networkFeatures: GeoJsonLineFeature[],
  leafGids: Set<number>
): void {
  if (!osmLeafDataSource) return
  osmLeafDataSource.clear()
  for (const f of networkFeatures) {
    const gid = f.properties?.gid
    if (gid == null || !leafGids.has(gid)) continue
    const feature = createLineFeature(f.geometry.coordinates, { gid })
    osmLeafDataSource.add(feature)
  }
  map?.triggerRepaint()
}

export function moveNetworkSegmentBelowLeafLayer(networkId: number): void {
  if (!map || !osmLeafDataSource) return
  const layerId = `${segmentsLayerId}-${networkId}`
  if (map.layers.getLayerById(layerId) && map.layers.getLayerById(osmLeafLayerId)) {
    map.layers.move(layerId, osmLeafLayerId)
  }
}
